import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { MarkdownText } from '../components/MarkdownText';
import { SignalTraceViewer } from '../components/SignalTraceViewer';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  CheckSquare,
  Code,
  ExternalLink,
  Sliders,
  FileText,
  Bot,
  Copy,
  Check,
  X,
  Info,
  Save,
  Download,
  ArrowLeft,
  Send,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Signal, ChatMessage, ReviewNote } from '../types';
import api from '../services/api';
import { getProductLineLabel } from '../constants/products';
import { useAuthStore } from '../stores/authStore';

function formatNoteTimestamp(isoString: string): string {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return isoString;
  }
}

function parseNotes(
  rawNotes?: string,
  signalCreatedAt?: string,
  signalUpdatedAt?: string
): ReviewNote[] {
  if (!rawNotes || !rawNotes.trim()) return [];
  try {
    const parsed = JSON.parse(rawNotes);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is ReviewNote =>
        Boolean(
          item &&
          typeof item === 'object' &&
          typeof item.id === 'string' &&
          typeof item.text === 'string'
        )
      );
    }
  } catch {
    // Non-JSON string -> legacy single note fallback
  }

  return [
    {
      id: 'legacy-note-1',
      text: rawNotes.trim(),
      author: 'Internal Reviewer',
      role: 'analyst',
      created_at: signalUpdatedAt || signalCreatedAt || new Date().toISOString(),
    },
  ];
}

const urgencyBadgeClasses: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-sky-100 text-sky-800 border-sky-200',
  low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

export default function SignalDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const currentUser = useAuthStore((state) => state.user);

  const [signal, setSignal] = useState<Signal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Raw API Payload Modal state
  const [showRawPayloadModal, setShowRawPayloadModal] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [showRationale, setShowRationale] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<ReviewNote[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesFeedback, setNotesFeedback] = useState<string | null>(null);

  // AI Assistant Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingQuery, setSendingQuery] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof chatEndRef.current?.scrollIntoView === 'function') {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, sendingQuery]);

  // Close Confirmation Modal state
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingSignal, setClosingSignal] = useState(false);

  const rawPayloadString = useMemo<string>(() => {
    if (!signal) return '';
    const rawData = signal.raw_payload;
    if (typeof rawData === 'string') {
      try {
        const parsed = JSON.parse(rawData);
        const target =
          parsed && typeof parsed === 'object' && 'raw' in parsed
            ? (parsed as Record<string, unknown>).raw
            : parsed;
        return JSON.stringify(target, null, 2);
      } catch {
        return rawData;
      }
    }

    if (rawData && typeof rawData === 'object') {
      const obj = rawData as Record<string, unknown>;
      const targetData = obj.raw || obj;
      return JSON.stringify(targetData, null, 2);
    }

    return JSON.stringify(
      {
        _notice: 'Seed data record (raw API response simulated)',
        signal_id: signal.signal_id || signal.id,
        source: signal.agency || signal.authority,
        source_url: signal.source_url,
        title: signal.title,
        content_detail: signal.content_detail || signal.full_analysis || signal.summary,
      },
      null,
      2
    );
  }, [signal]);

  const handleCopyRawPayload = () => {
    if (!rawPayloadString) return;
    navigator.clipboard.writeText(rawPayloadString);
    setCopiedPayload(true);
    setTimeout(() => setCopiedPayload(false), 2000);
  };

  const fetchSignal = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.signals.getById(id);
      setSignal(data);
      setNotes(parseNotes(data.notes, data.created_at, data.updated_at));
      setChatMessages([
        {
          id: 'init-msg',
          role: 'assistant',
          content: `Hello! I am your AI Signal Assistant contextually bound to signal ${data.id}. Ask me anything about reporting timelines, affected product lines, or compliance requirements.`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to load signal detail');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSignal();
  }, [fetchSignal]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleUpdateTriage = async (newStatus: Signal['triage_status']) => {
    if (!signal) return;
    try {
      const updated = await api.signals.updateTriage(signal.id, newStatus);
      setSignal(updated);
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to update triage status');
    }
  };

  const handleTriageClick = (st: Signal['triage_status']) => {
    if (st === 'closed') {
      setShowCloseModal(true);
      return;
    }
    handleUpdateTriage(st);
  };

  const handleConfirmClose = async () => {
    if (!signal) return;
    try {
      setClosingSignal(true);
      await api.signals.updateTriage(signal.id, 'closed');
      setShowCloseModal(false);
      navigate('/dashboard');
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to close signal');
    } finally {
      setClosingSignal(false);
    }
  };

  const handleAddNote = async () => {
    if (!signal || !newNoteText.trim() || savingNotes) return;
    const newNote: ReviewNote = {
      id: `note-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      text: newNoteText.trim(),
      author: currentUser?.email || 'Internal Reviewer',
      role: currentUser?.role || 'analyst',
      created_at: new Date().toISOString(),
    };
    const updatedNotes = [...notes, newNote];
    try {
      setSavingNotes(true);
      setNotesFeedback(null);
      const updated = await api.signals.updateNotes(signal.id, JSON.stringify(updatedNotes));
      setSignal(updated);
      setNotes(updatedNotes);
      setNewNoteText('');
      setNotesFeedback('Note saved successfully');
      setTimeout(() => setNotesFeedback(null), 3000);
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to save note');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStartEdit = (note: ReviewNote) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text);
    setDeletingNoteId(null);
  };

  const handleSaveEdit = async (noteId: string) => {
    if (!signal || !editingNoteText.trim() || savingNotes) return;
    const updatedNotes = notes.map((n) =>
      n.id === noteId
        ? { ...n, text: editingNoteText.trim(), updated_at: new Date().toISOString() }
        : n
    );
    try {
      setSavingNotes(true);
      setNotesFeedback(null);
      const updated = await api.signals.updateNotes(signal.id, JSON.stringify(updatedNotes));
      setSignal(updated);
      setNotes(updatedNotes);
      setEditingNoteId(null);
      setEditingNoteText('');
      setNotesFeedback('Note updated successfully');
      setTimeout(() => setNotesFeedback(null), 3000);
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to update note');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!signal || savingNotes) return;
    const updatedNotes = notes.filter((n) => n.id !== noteId);
    try {
      setSavingNotes(true);
      setNotesFeedback(null);
      const updated = await api.signals.updateNotes(signal.id, JSON.stringify(updatedNotes));
      setSignal(updated);
      setNotes(updatedNotes);
      setDeletingNoteId(null);
      setNotesFeedback('Note deleted successfully');
      setTimeout(() => setNotesFeedback(null), 3000);
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to delete note');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSendChatMessage = async (overridePrompt?: string) => {
    const question = overridePrompt || chatInput.trim();
    if (!question || sendingQuery) return;

    const userMsg: ChatMessage = {
      id: `usr-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date().toLocaleTimeString(),
    };

    setChatMessages((prev) => [...prev, userMsg]);
    if (!overridePrompt) setChatInput('');
    setSendingQuery(true);

    try {
      const res = await api.query.ask(question, signal?.id);
      const assistantMsg: ChatMessage = {
        id: `ast-${Date.now()}`,
        role: 'assistant',
        content: res.data.answer,
        sources:
          Array.isArray(res.data.sources) && res.data.sources.length > 0
            ? res.data.sources.filter((s) => s?.signal_id)
            : signal?.source_url
              ? [{ signal_id: signal.id, source_url: signal.source_url, authority: signal.agency }]
              : [],
        timestamp: new Date().toLocaleTimeString(),
      };
      setChatMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      console.error('[chat] agent call failed:', err);
      const fallbackMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error reaching the assistant. Please try again.',
        sources: [],
        timestamp: new Date().toLocaleTimeString(),
      };
      setChatMessages((prev) => [...prev, fallbackMsg]);
    } finally {
      setSendingQuery(false);
    }
  };

  const parsedFullAnalysis = useMemo((): {
    root_cause?: string;
    patient_risk?: string;
    regulatory_implication?: string;
    spacelabs_relevance?: string;
  } | null => {
    if (!signal?.full_analysis) return null;
    try {
      const parsed = JSON.parse(signal.full_analysis);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
      return null;
    } catch {
      return null;
    }
  }, [signal?.full_analysis]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-xs text-slate-500 font-medium">
            Loading signal intelligence detail...
          </p>
        </div>
      </div>
    );
  }

  if (error || !signal) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-xl mx-auto bg-white rounded-2xl p-6 border border-red-200 text-center shadow-sm">
          <h2 className="text-lg font-bold text-red-800">Signal Not Found</h2>
          <p className="text-xs text-slate-600 mt-2">
            {error || 'The requested signal does not exist.'}
          </p>
          <button
            onClick={handleBack}
            className="mt-4 bg-indigo-600 text-white text-xs font-semibold px-4 py-2 rounded-xl"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const actionItemsList = signal.action_items || signal.recommendations || [];
  const fdaMetadata = signal.fda_metadata || {};

  // Extract clean structured details from raw_payload or content_detail
  const rawObj =
    signal.raw_payload && typeof signal.raw_payload === 'object'
      ? (signal.raw_payload as Record<string, unknown>)
      : null;
  const innerRaw =
    rawObj && 'raw' in rawObj && typeof rawObj.raw === 'object'
      ? (rawObj.raw as Record<string, unknown>)
      : rawObj;

  const structuredFields: Array<{ label: string; value: string; isLong?: boolean }> = [];

  const formatFdaValue = (value: unknown): string => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const fdaAddress = fdaMetadata.firm_address;
  const addressValue =
    fdaAddress && typeof fdaAddress === 'object' && !Array.isArray(fdaAddress)
      ? Object.values(fdaAddress as Record<string, unknown>)
          .map(formatFdaValue)
          .filter((value) => value && value.toUpperCase() !== 'N/A')
          .join(', ')
      : '';
  const fdaFields: Array<{ label: string; value: string; isLong?: boolean }> = [
    { label: 'Event Type', value: formatFdaValue(fdaMetadata.event_type) },
    { label: 'FDA Product Problems', value: formatFdaValue(fdaMetadata.product_problems) },
    { label: 'Patient Outcomes', value: formatFdaValue(fdaMetadata.patient_outcomes) },
    { label: 'Patient Problems', value: formatFdaValue(fdaMetadata.patient_problems) },
    { label: 'Device Model', value: formatFdaValue(fdaMetadata.model_number) },
    {
      label: 'FDA Product Code',
      value: formatFdaValue(fdaMetadata.device_report_product_code || fdaMetadata.product_code),
    },
    {
      label: 'FDA Device Class',
      value: formatFdaValue(fdaMetadata.device_class || fdaMetadata.classification),
    },
    { label: 'UDI-DI', value: formatFdaValue(fdaMetadata.udi_di) },
    {
      label: 'Recall Status',
      value: formatFdaValue(fdaMetadata.recall_status || fdaMetadata.status),
    },
    { label: 'Product Quantity', value: formatFdaValue(fdaMetadata.product_quantity) },
    { label: 'Remedial Action', value: formatFdaValue(fdaMetadata.remedial_action) },
    { label: 'Report Source', value: formatFdaValue(fdaMetadata.report_source_code) },
    { label: 'Manufacturer Report Type', value: formatFdaValue(fdaMetadata.mfr_report_type) },
    {
      label: 'Recall Initiated',
      value: formatFdaValue(fdaMetadata.event_date_initiated || fdaMetadata.recall_initiation_date),
    },
    {
      label: 'FDA Classification Date',
      value: formatFdaValue(fdaMetadata.center_classification_date),
    },
    { label: 'Firm Address', value: addressValue },
  ].filter((field) => field.value && field.value.toUpperCase() !== 'N/A');

  // 1. Recalling Firm
  const firm =
    (typeof innerRaw?.recalling_firm === 'string' && innerRaw.recalling_firm) ||
    (typeof innerRaw?.manufacturer_name === 'string' && innerRaw.manufacturer_name) ||
    (typeof innerRaw?.applicant === 'string' && innerRaw.applicant);
  if (firm && firm.trim()) {
    structuredFields.push({ label: 'Recalling Firm / Entity', value: firm.trim() });
  }

  // 2. Reason for Recall
  const reason =
    (typeof innerRaw?.reason_for_recall === 'string' && innerRaw.reason_for_recall) ||
    (typeof innerRaw?.decision_description === 'string' && innerRaw.decision_description);
  if (reason && reason.trim()) {
    structuredFields.push({
      label: 'Reason for Regulatory Action',
      value: reason.trim(),
      isLong: true,
    });
  }

  // 3. Product Description
  const desc =
    (typeof innerRaw?.product_description === 'string' && innerRaw.product_description) ||
    (typeof innerRaw?.device_name === 'string' && innerRaw.device_name);
  if (desc && desc.trim()) {
    structuredFields.push({
      label: 'Device Specifications & Model Description',
      value: desc.trim(),
      isLong: true,
    });
  }

  // 4. Root Cause
  const rootCause =
    typeof innerRaw?.root_cause_description === 'string' && innerRaw.root_cause_description;
  if (rootCause && rootCause.trim()) {
    structuredFields.push({ label: 'Identified Root Cause', value: rootCause.trim() });
  }

  // 5. Manufacturer Action
  const action = typeof innerRaw?.action === 'string' && innerRaw.action;
  if (action && action.trim()) {
    structuredFields.push({
      label: 'Manufacturer Corrective Action & Customer Instructions',
      value: action.trim(),
      isLong: true,
    });
  }

  // 6. Distribution Pattern
  const distribution =
    typeof innerRaw?.distribution_pattern === 'string' && innerRaw.distribution_pattern;
  if (distribution && distribution.trim()) {
    structuredFields.push({
      label: 'Distribution Pattern',
      value: distribution.trim(),
      isLong: true,
    });
  }

  const isJsonString = (str?: string | null): boolean => {
    if (!str) return false;
    const t = str.trim();
    return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
  };

  const cleanNoticeContent =
    (signal.content_detail && !isJsonString(signal.content_detail) && signal.content_detail) ||
    (typeof innerRaw?.summary === 'string' &&
      !isJsonString(innerRaw.summary) &&
      innerRaw.summary) ||
    (typeof innerRaw?.description === 'string' &&
      !isJsonString(innerRaw.description) &&
      innerRaw.description) ||
    signal.summary ||
    '';

  // If no FDA-specific recall fields were found, extract structured details from authority notice facts
  if (structuredFields.length === 0) {
    // 1. Issuing Regulatory Authority
    const authorityName =
      signal.agency ||
      signal.authority ||
      (typeof innerRaw?.source === 'string' ? innerRaw.source : null);
    const authorityDisplay = authorityName || 'Regulatory Authority';
    structuredFields.push({ label: 'Issuing Regulatory Authority', value: authorityDisplay });

    // 2. Official Notice Reference / Identifier
    const rawTitle = (typeof innerRaw?.title === 'string' && innerRaw.title) || signal.title || '';
    const refMatch = rawTitle.match(
      /\b(?:DSI|MDA|EL|NatPSA|NPSA)[\w/(-]+|\b[A-Z0-9]+-[A-Z0-9]+-\d+/i
    );
    const noticeRef =
      (refMatch && refMatch[0].replace(/[()]/g, '')) ||
      (typeof innerRaw?.id === 'string' && !innerRaw.id.startsWith('tag:') ? innerRaw.id : null) ||
      signal.signal_id;
    if (noticeRef && noticeRef.trim()) {
      structuredFields.push({ label: 'Notice Reference / Identifier', value: noticeRef.trim() });
    }

    // 3. Reason for Regulatory Action
    let reasonText = '';
    if (typeof innerRaw?.reason_for_recall === 'string' && innerRaw.reason_for_recall.trim()) {
      reasonText = innerRaw.reason_for_recall.trim();
    } else if (
      typeof innerRaw?.decision_description === 'string' &&
      innerRaw.decision_description.trim()
    ) {
      reasonText = innerRaw.decision_description.trim();
    } else if (
      typeof innerRaw?.summary === 'string' &&
      innerRaw.summary.trim() &&
      innerRaw.summary.trim() !== signal.title?.trim()
    ) {
      reasonText = innerRaw.summary.trim();
    } else if (
      typeof innerRaw?.description === 'string' &&
      innerRaw.description.trim() &&
      innerRaw.description.trim() !== signal.title?.trim()
    ) {
      reasonText = innerRaw.description.trim();
    }

    if (reasonText) {
      structuredFields.push({
        label: 'Reason for Regulatory Action',
        value: reasonText,
        isLong: true,
      });
    }

    // 4. Device Specifications & Affected Products
    const productsList =
      Array.isArray(signal.products) && signal.products.length > 0
        ? signal.products.filter((p) => p && p !== 'Portfolio General').join(', ')
        : '';
    const deviceSpec =
      productsList ||
      (typeof innerRaw?.product_description === 'string' && innerRaw.product_description) ||
      (typeof innerRaw?.device_name === 'string' && innerRaw.device_name);
    if (deviceSpec && deviceSpec.trim()) {
      structuredFields.push({
        label: 'Device Specifications & Model Description',
        value: deviceSpec.trim(),
        isLong: true,
      });
    }

    // 5. Official Authority Notice Details
    if (
      cleanNoticeContent &&
      cleanNoticeContent.trim() &&
      cleanNoticeContent.trim() !== reasonText?.trim() &&
      cleanNoticeContent.trim() !== signal.title?.trim()
    ) {
      structuredFields.push({
        label: 'Official Authority Notice Details',
        value: cleanNoticeContent.trim(),
        isLong: true,
      });
    }

    // 6. Target Jurisdiction & Market
    const market =
      (Array.isArray(signal.markets) && signal.markets.length > 0 && signal.markets.join(', ')) ||
      '';
    if (market) {
      structuredFields.push({ label: 'Target Jurisdiction & Market', value: market });
    }
  }

  // Clean, non-template executive summary
  const isTemplateText = (t?: string | null) =>
    !t ||
    t.trim().toLowerCase() === 'null' ||
    t.trim().toLowerCase() === 'none' ||
    t.trim().toLowerCase() === 'undefined' ||
    t.includes('Recalling Firm:\nReason for Recall:') ||
    t.includes('Recalling Firm: \nReason for Recall:') ||
    t.trim().length === 0;

  let completeSummary = signal.summary;
  if (isTemplateText(completeSummary)) {
    if (innerRaw?.reason_for_recall && typeof innerRaw.reason_for_recall === 'string') {
      completeSummary = innerRaw.reason_for_recall;
    } else if (signal.content_detail && !isTemplateText(signal.content_detail)) {
      completeSummary = signal.content_detail;
    } else {
      completeSummary = signal.title || 'Regulatory intelligence notice requiring RAQA assessment.';
    }
  }

  const handleExportPdf = () => {
    window.print();
  };

  const renderChatBody = () => (
    <>
      {/* Chat Message History */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-white">
        {chatMessages.map((msg) => (
          <div
            key={msg.id}
            className={`flex flex-col text-xs ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`p-3.5 rounded-2xl max-w-[88%] leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-700 text-white rounded-br-xs font-medium'
                  : 'bg-white border border-slate-200 text-slate-800 shadow-none rounded-bl-xs'
              }`}
            >
              <MarkdownText
                content={msg.content}
                sources={
                  msg.sources as
                    | {
                        signal_id: string;
                        source_url?: string;
                        authority?: string;
                        title?: string;
                      }[]
                    | undefined
                }
              />
              {msg.sources && msg.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-blue-700 font-medium flex items-center gap-1 flex-wrap">
                  {(
                    msg.sources as {
                      signal_id: string;
                      source_url?: string;
                      authority?: string;
                      title?: string;
                    }[]
                  ).map((src, i) => (
                    <a
                      key={i}
                      href={src.source_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-blue-900 flex items-center gap-1"
                    >
                      <span>
                        📄 [{i + 1}] {src.authority || src.title || 'Source'}
                      </span>
                      <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <span className="text-[10px] text-slate-400 mt-1 px-1 font-mono tabular-nums">
              {msg.timestamp}
            </span>
          </div>
        ))}

        {sendingQuery && (
          <div className="flex items-center gap-2 text-xs text-slate-600 p-2.5 bg-white rounded-lg border border-slate-200 w-fit shadow-xs">
            <div className="w-3 h-3 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
            Evaluating regulatory context...
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Quick Question Suggestion Chips */}
      <div className="px-4 py-2 border-t border-slate-100 bg-white flex flex-wrap gap-1.5 shrink-0">
        <button
          onClick={() => handleSendChatMessage('What products are affected by this signal?')}
          className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded-md border border-slate-200 transition font-medium cursor-pointer"
        >
          + Affected Products?
        </button>
        <button
          onClick={() => handleSendChatMessage('What are the reporting timelines?')}
          className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded-md border border-slate-200 transition font-medium cursor-pointer"
        >
          + Reporting Timelines?
        </button>
        <button
          onClick={() => handleSendChatMessage('Provide a high-level summary for QA.')}
          className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded-md border border-slate-200 transition font-medium cursor-pointer"
        >
          + Summary for QA
        </button>
      </div>

      {/* Input Bar */}
      <div className="p-4 bg-white border-t border-slate-100 shrink-0">
        <div className="border border-slate-200 rounded-2xl p-3 bg-white shadow-xs focus-within:border-slate-400 transition flex flex-col gap-2">
          <input
            type="text"
            placeholder="Ask AI Signal Assistant..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendChatMessage()}
            className="w-full text-xs bg-transparent border-0 focus:outline-none focus:ring-0 p-0 text-slate-800 placeholder-slate-400 font-sans"
          />
          <div className="flex items-center justify-end">
            <button
              onClick={() => handleSendChatMessage()}
              disabled={sendingQuery || !chatInput.trim()}
              className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold p-2 rounded-lg transition disabled:opacity-40 shadow-xs cursor-pointer"
              title="Send message"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans flex flex-col lg:flex-row">
      {/* Main Left Content Area */}
      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 max-w-5xl">
        {/* Navigation Breadcrumb, Signal ID & Page Actions */}
        <div className="no-print mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              aria-label="Back to Digest"
              className="inline-flex items-center gap-1.5 font-semibold text-slate-700 hover:text-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1.5 py-1 cursor-pointer text-xs"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span>Signal ID:</span>
              <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono text-[11px] select-all font-medium">
                {signal.signal_id || signal.id}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleExportPdf}
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-800 shadow-xs transition hover:bg-slate-50 active:scale-[0.98] cursor-pointer"
            title="Print regulatory dossier or export to PDF"
          >
            <Download className="h-3.5 w-3.5 text-slate-700" />
            <span>Export PDF Report</span>
          </button>
        </div>

        {/* Print-Only Executive Header */}
        <div className="print-only mb-6 border-b-2 border-slate-900 pb-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-base font-black tracking-widest text-slate-900 uppercase">
                SPACELABS HEALTHCARE
              </div>
              <div className="text-xs text-slate-600 font-semibold tracking-wide uppercase mt-0.5">
                Regulatory Intelligence Brief & Compliance Dossier
              </div>
            </div>
            <div className="text-right text-[11px] text-slate-500 font-mono">
              <div>CONFIDENTIAL / REGULATORY COMPLIANCE</div>
              <div>Generated: {new Date().toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Section 1: Hero Header Card */}
          <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="bg-slate-100 text-slate-800 px-2.5 py-0.5 rounded font-semibold border border-slate-200 uppercase tracking-wider text-[11px]">
                {signal.agency || signal.authority || 'FDA CDRH'}
              </span>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => signal.rationale && setShowRationale((v) => !v)}
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded font-bold uppercase text-[10px] tracking-wider border ${
                    (signal.urgency && urgencyBadgeClasses[signal.urgency]) ||
                    'bg-slate-100 text-slate-800'
                  } ${signal.rationale ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                  title={signal.rationale ? 'Click to see classification rationale' : undefined}
                >
                  {(signal.urgency || 'Unrated').toUpperCase()} PRIORITY
                  {signal.rationale && <Info size={11} aria-hidden="true" />}
                </button>
                {showRationale && signal.rationale && (
                  <div className="absolute left-0 top-full mt-1 z-50 w-80 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-700">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-semibold text-slate-800 text-[11px] uppercase tracking-wider">
                        Classification Rationale
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowRationale(false)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X size={12} />
                      </button>
                    </div>
                    <p className="leading-relaxed text-slate-600">{signal.rationale}</p>
                  </div>
                )}
              </div>
              <span className="bg-slate-50 text-slate-600 px-2.5 py-0.5 rounded border border-slate-200 capitalize text-xs font-medium">
                {signal.type}
              </span>
              <span className="text-slate-500 text-xs ml-auto font-mono tabular-nums">
                Published:{' '}
                {new Date(
                  signal.published_date || signal.published_at || signal.created_at
                ).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>

            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight tracking-[-0.01em]">
              {signal.title || signal.summary}
            </h1>

            <div className="flex flex-wrap items-center justify-between pt-3 border-t border-slate-100 text-xs text-slate-600 gap-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div>
                  <span className="font-semibold text-slate-600">Product Line: </span>
                  <span className="text-slate-900 font-medium">
                    {getProductLineLabel(signal.product_line ?? 'portfolio_general')}
                  </span>
                </div>
                {signal.products && signal.products.length > 0 && (
                  <div>
                    <span className="font-semibold text-slate-600">Affected Devices: </span>
                    <span className="text-slate-900 font-medium">{signal.products.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: AI Executive Summary & Clinical Assessment */}
          <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-3">
            <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-700 shrink-0" />
              <span>AI Executive Summary & Clinical Assessment</span>
            </h2>
            <div className="text-xs text-slate-800 leading-relaxed bg-slate-50/80 p-4 rounded-lg border border-slate-200 font-sans">
              <MarkdownText content={completeSummary} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              {[
                {
                  label: 'Root Cause',
                  value: parsedFullAnalysis?.root_cause || 'Not available',
                  cls: 'bg-red-50/40 border-red-100',
                },
                {
                  label: 'Patient Risk',
                  value: parsedFullAnalysis?.patient_risk || 'Not available',
                  cls: 'bg-orange-50/40 border-orange-100',
                },
                {
                  label: 'Regulatory Implication',
                  value: parsedFullAnalysis?.regulatory_implication || 'Not available',
                  cls: 'bg-blue-50/40 border-blue-100',
                },
                {
                  label: 'Spacelabs Relevance',
                  value: parsedFullAnalysis?.spacelabs_relevance || 'Not available',
                  cls: 'bg-violet-50/40 border-violet-100',
                },
              ].map((section) => (
                <div
                  key={section.label}
                  className={`rounded-lg p-3.5 border text-xs space-y-1 ${section.cls}`}
                >
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider font-mono">
                    {section.label}
                  </div>
                  <div className="text-slate-800 leading-relaxed font-sans">{section.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Structured Regulatory Notice & Device Details */}
          {structuredFields.length > 0 ? (
            <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-4">
              <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-700 shrink-0" />
                <span>Official Authority Notice & Technical Details</span>
              </h2>

              <div className="space-y-3">
                {structuredFields.map((field, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-50/60 rounded-lg p-3.5 border border-slate-200/70 text-xs space-y-1"
                  >
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider font-mono">
                      {field.label}
                    </div>
                    <div className="text-slate-800 leading-relaxed whitespace-pre-line font-sans">
                      {field.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : cleanNoticeContent && !cleanNoticeContent.includes('Recalling Firm:\nReason') ? (
            <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-3">
              <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-700 shrink-0" />
                <span>Official Authority Notice & Technical Details</span>
              </h2>
              <div className="text-xs text-slate-800 leading-relaxed bg-slate-50/80 p-4 rounded-lg border border-slate-200 whitespace-pre-line font-sans">
                {cleanNoticeContent}
              </div>
            </div>
          ) : null}

          {fdaFields.length > 0 ? (
            <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-4">
              <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-700 shrink-0" />
                <span>FDA Evidence</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fdaFields.map((field) => (
                  <div
                    key={field.label}
                    className="bg-blue-50/40 rounded-lg p-3.5 border border-blue-100 text-xs space-y-1"
                  >
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider font-mono">
                      {field.label}
                    </div>
                    <div className="text-slate-800 leading-relaxed whitespace-pre-line font-sans">
                      {field.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Section 4: Recommended Action Items (RAQA) */}
          <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-3">
            <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-amber-700 shrink-0" />
              <span>Recommended Action Items (RAQA)</span>
            </h2>
            {actionItemsList.length > 0 ? (
              <ul className="space-y-2">
                {actionItemsList.map((item: string, idx: number) => (
                  <li
                    key={idx}
                    className="text-xs text-amber-950 bg-amber-50/70 border border-amber-200/80 p-3.5 rounded-lg flex items-start gap-2.5 font-medium leading-relaxed"
                  >
                    <span className="bg-amber-700 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500 italic bg-slate-50 p-3 rounded-lg border border-slate-200">
                No specific action items generated for this notice.
              </p>
            )}
          </div>

          {/* Section 5: Official Source Link & Raw API Payload Controls */}
          <div className="bg-slate-50/90 border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 overflow-hidden shadow-xs">
            <div className="text-xs text-slate-900 min-w-0 flex-1">
              <div className="font-bold flex items-center gap-1.5 flex-wrap">
                <span>Regulatory Source Information</span>
                <span className="text-[10px] bg-blue-50 text-blue-900 px-2 py-0.5 rounded font-mono border border-blue-200">
                  {signal.agency || signal.authority || 'FDA CDRH'}
                </span>
              </div>
              {signal.source_url ? (
                <div
                  className="text-[11px] text-blue-700 truncate mt-0.5 font-mono"
                  title={signal.source_url}
                >
                  {signal.source_url}
                </div>
              ) : (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Direct Regulatory Feed Stream
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 w-full md:w-auto">
              <button
                onClick={() => setShowRawPayloadModal(true)}
                className="bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-800 text-xs font-semibold px-3.5 py-2 rounded-lg border border-slate-300 transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <Code className="w-3.5 h-3.5 text-slate-600" />
                <span>View Raw API Payload</span>
              </button>
              {signal.source_url && (
                <a
                  href={signal.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-4 py-2 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 whitespace-nowrap text-center cursor-pointer"
                >
                  <span>View External Source</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>

          {/* Triage Status Handoff */}
          <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-3">
            <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
              <Sliders className="w-4 h-4 text-blue-700 shrink-0" />
              <span>Triage Status Handoff</span>
            </h2>
            <div className="flex flex-wrap gap-3">
              {(
                [
                  { key: 'requires_action', label: 'Take Action' },
                  { key: 'monitoring', label: 'Monitor' },
                  { key: 'closed', label: 'Close' },
                ] as const
              ).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleTriageClick(key as Signal['triage_status'])}
                  className={`text-xs font-semibold px-4 py-2 rounded-lg border transition cursor-pointer active:scale-[0.98] ${
                    signal.triage_status === key
                      ? 'bg-blue-700 text-white border-blue-700 shadow-xs'
                      : 'bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Internal Review Notes Section (Persistent in DB) */}
          <div className="bg-white rounded-xl p-6 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600 shrink-0" />
                <span>Internal RAQA Review Notes</span>
                {notes.length > 0 && (
                  <span className="text-xs bg-slate-100 text-slate-700 font-semibold px-2 py-0.5 rounded-full border border-slate-200/60">
                    {notes.length}
                  </span>
                )}
              </h2>
              {notesFeedback && (
                <span className="text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded flex items-center gap-1">
                  <Check className="w-3 h-3 text-emerald-600" /> {notesFeedback}
                </span>
              )}
            </div>

            {/* Comment Blocks List */}
            <div className="space-y-3">
              {notes.length === 0 ? (
                <div className="text-center py-5 text-xs text-slate-400 italic bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                  No internal notes posted yet. Add a note below to record audit observations.
                </div>
              ) : (
                notes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-slate-50/70 rounded-lg p-3.5 border border-slate-200/80 space-y-2 transition hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                          {(note.author?.[0] || 'U').toUpperCase()}
                        </div>
                        <span className="text-xs font-semibold text-slate-900 truncate">
                          {note.author || 'Internal Reviewer'}
                        </span>
                        {note.role && (
                          <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-slate-200/80 text-slate-700 font-medium">
                            {note.role}
                          </span>
                        )}
                        <span className="text-[11px] text-slate-400 shrink-0">
                          • {formatNoteTimestamp(note.created_at)}
                        </span>
                        {note.updated_at && (
                          <span
                            className="text-[10px] text-slate-400 italic shrink-0"
                            title={`Edited ${formatNoteTimestamp(note.updated_at)}`}
                          >
                            (edited)
                          </span>
                        )}
                      </div>

                      {/* Edit / Delete actions (hidden in print) */}
                      <div className="no-print flex items-center gap-1 shrink-0">
                        {editingNoteId !== note.id && (
                          <>
                            <button
                              onClick={() => handleStartEdit(note)}
                              disabled={savingNotes}
                              title="Edit note"
                              aria-label="Edit note"
                              className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded transition cursor-pointer"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() =>
                                setDeletingNoteId(deletingNoteId === note.id ? null : note.id)
                              }
                              disabled={savingNotes}
                              title="Delete note"
                              aria-label="Delete note"
                              className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Delete confirmation inline prompt */}
                    {deletingNoteId === note.id && (
                      <div className="no-print bg-red-50 border border-red-200 rounded-md p-2.5 flex items-center justify-between gap-3 text-xs">
                        <span className="text-red-800 font-medium">
                          Are you sure you want to delete this note?
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            disabled={savingNotes}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-2.5 py-1 rounded text-xs transition cursor-pointer shadow-xs"
                          >
                            {savingNotes ? 'Deleting...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setDeletingNoteId(null)}
                            disabled={savingNotes}
                            className="text-slate-600 hover:text-slate-900 px-2 py-1 rounded text-xs transition cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Edit Mode vs View Mode */}
                    {editingNoteId === note.id ? (
                      <div className="space-y-2 pt-1">
                        <textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={3}
                          className="w-full text-xs bg-white border border-blue-400 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 font-sans transition"
                          autoFocus
                        />
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingNoteText('');
                            }}
                            disabled={savingNotes}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition cursor-pointer"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveEdit(note.id)}
                            disabled={savingNotes || !editingNoteText.trim()}
                            className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50 shadow-xs flex items-center gap-1 cursor-pointer"
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span>{savingNotes ? 'Saving...' : 'Save'}</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">
                        {note.text}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add Note Composer */}
            <div className="space-y-2.5 pt-2 border-t border-slate-100">
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Type internal regulatory review notes, audit observations, or team instructions..."
                rows={3}
                className="w-full text-xs bg-slate-50/80 border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white font-sans transition"
              />
              <div className="flex justify-end">
                <button
                  onClick={handleAddNote}
                  disabled={savingNotes || !newNoteText.trim()}
                  className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-4 py-2 rounded-lg transition disabled:opacity-50 shadow-xs flex items-center gap-1.5 cursor-pointer"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{savingNotes ? 'Saving...' : 'Save Internal Note'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Pipeline Execution Traces & Audit Logs Section */}
          <SignalTraceViewer signalId={signal.id} signalExternalId={signal.signal_id} />
        </div>
      </main>

      {/* RIGHT COLUMN: AI Signal Assistant Sidebar (Desktop only) */}
      <aside className="no-print hidden lg:flex flex-col w-96 xl:w-[400px] shrink-0 border-l border-slate-200 bg-white sticky top-0 h-screen overflow-hidden">
        {/* Sidebar Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-white shrink-0">
          <h3 className="text-sm font-semibold text-slate-800 font-sans">AI Signal Assistant</h3>
        </div>

        {renderChatBody()}
      </aside>

      {/* Raw API Payload Modal */}
      {showRawPayloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs animate-fade-in no-print">
          <div className="bg-slate-900 border border-slate-800 text-slate-100 rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-950">
              <div className="flex items-center gap-3">
                <span className="bg-blue-500/20 text-blue-400 font-mono text-xs px-2.5 py-1 rounded-md border border-blue-500/30 font-bold flex items-center gap-1.5">
                  <Code className="w-3.5 h-3.5" />
                  <span>RAW API PAYLOAD</span>
                </span>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    Ingested Regulatory Source Payload
                  </h3>
                  <p className="text-[11px] text-slate-400 font-mono">
                    {signal.agency || signal.authority || 'FDA CDRH'} | Signal ID: {signal.id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRawPayloadModal(false)}
                className="text-slate-400 hover:text-white text-base font-bold w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body: Code Viewer */}
            <div className="p-4 flex-1 overflow-y-auto bg-slate-950/80">
              <pre className="text-xs font-mono text-emerald-400 bg-slate-950 p-4 rounded-lg border border-slate-800 leading-relaxed overflow-x-auto whitespace-pre-wrap select-all">
                {rawPayloadString}
              </pre>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
              <span className="text-[11px] text-slate-400 font-mono">
                Format: JSON | Authority: {signal.agency || 'API Endpoint'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyRawPayload}
                  className="bg-slate-800 hover:bg-slate-700 active:scale-[0.98] text-slate-200 text-xs font-semibold px-4 py-2 rounded-lg border border-slate-700 transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  {copiedPayload ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span>Copied to Clipboard!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Raw JSON</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowRawPayloadModal(false)}
                  className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-4 py-2 rounded-lg transition cursor-pointer shadow-xs"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Floating Action Button for AI Assistant (< lg) */}
      <button
        type="button"
        onClick={() => setMobileChatOpen(true)}
        className="fixed bottom-20 right-4 z-40 flex items-center gap-2 bg-blue-700 hover:bg-blue-800 active:scale-95 text-white px-4 py-2.5 rounded-full shadow-lg border border-blue-600 transition lg:hidden no-print cursor-pointer"
        aria-label="Open AI Assistant chat"
      >
        <Bot className="w-4 h-4 text-blue-200" />
        <span className="text-xs font-semibold">Ask Assistant</span>
        {chatMessages.length > 1 && (
          <span className="bg-white text-blue-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center font-mono">
            {chatMessages.length - 1}
          </span>
        )}
      </button>

      {/* Mobile Slide-Up Drawer for AI Assistant (< lg) */}
      {mobileChatOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-slate-950/50 backdrop-blur-xs animate-fade-in lg:hidden no-print">
          <div
            className="fixed inset-0"
            onClick={() => setMobileChatOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-lg mx-auto bg-white rounded-t-2xl shadow-2xl flex flex-col h-[85vh] max-h-[85vh] overflow-hidden border-t border-slate-200 z-10">
            {/* Drawer Header with Close Button */}
            <div className="p-3.5 border-b border-slate-200 bg-white text-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800 font-sans">
                AI Signal Assistant
              </h3>
              <button
                type="button"
                onClick={() => setMobileChatOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
                aria-label="Close AI Assistant chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {renderChatBody()}
          </div>
        </div>
      )}

      {/* Close Confirmation Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-fade-in no-print">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shrink-0">
                <Info className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Close Regulatory Signal</h3>
                <p className="text-xs text-slate-500 mt-0.5">Archive action confirmation</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to close this signal? Closing will archive and hide this signal
              from the active surveillance dashboard.
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowCloseModal(false)}
                disabled={closingSignal}
                className="px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClose}
                disabled={closingSignal}
                className="px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-xs transition cursor-pointer disabled:opacity-50"
              >
                {closingSignal ? 'Closing...' : 'Confirm & Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
